import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FiPlus,
  FiShoppingCart,
  FiUser,
  FiUsers,
  FiFilter,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import { Card, CardBody } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import DataTable from '../../components/ui/DataTable';
import SearchInput from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Input';
import EmptyState from '../../components/ui/EmptyState';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { useAuth } from '../../auth/AuthContext';

import {
  listMyProcurementPaginated,
  listProcurementPaginated,
  listProcurementFilterPaginated,
} from '../../api/procurement';
import { listActiveVendors } from '../../api/vendors';
import { WorkflowStatus } from '../../enums';
import ProcurementForm from './ProcurementForm';
import ProcurementDetailDrawer from './ProcurementDetailDrawer';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...Object.values(WorkflowStatus).map((v) => ({
    value: v,
    label: v.replace(/_/g, ' '),
  })),
];

function SummaryStat({ label, value, icon: Icon, tone = 'primary' }) {
  const tones = {
    primary: 'bg-primary/10 text-primary',
    accent: 'bg-accent/20 text-primary-dark',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    info: 'bg-info/10 text-info',
  };
  return (
    <Card>
      <CardBody className="flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-lg ${tones[tone]}`}>
          <Icon />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            {label}
          </div>
          <div className="text-lg font-semibold text-ink">{value}</div>
        </div>
      </CardBody>
    </Card>
  );
}

export default function ProcurementListPage() {
  const { isAdmin, hasDept, isSuperadmin } = useAuth();

  // Admins in PROCUREMENT dept (or superadmin) can see the full list.
  const isProcAdmin =
    isSuperadmin || (isAdmin && hasDept('PROCUREMENT'));

  const [scope, setScope] = useState(isProcAdmin ? 'all' : 'mine');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Vendor name lookup (cached globally)
  const { data: vendors } = useQuery({
    queryKey: ['vendors', 'active'],
    queryFn: listActiveVendors,
    staleTime: 60_000,
  });
  const vendorMap = useMemo(() => {
    const m = new Map();
    (vendors || []).forEach((v) => m.set(v.id, v.name));
    return m;
  }, [vendors]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const queryKey = ['procurement', scope, statusFilter, page, pageSize];

  const fetchProcurement = async ({ queryKey }) => {
    const [_key, qScope, qStatus, qPage, qSize] = queryKey;
    const params = { page: qPage, size: qSize };
    
    if (qStatus) {
      params.status = qStatus;
      // Workaround for backend JDBC issue with null dates in Postgres
      params.startDate = '2000-01-01';
      params.endDate = '2099-12-31';
      return listProcurementFilterPaginated(params);
    }
    return qScope === 'mine' ? listMyProcurementPaginated(qPage, qSize) : listProcurementPaginated(qPage, qSize);
  };

  const {
    data: pageData,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: fetchProcurement,
    enabled: scope === 'all' ? isProcAdmin : true,
  });

  const rawList = pageData?.content || (Array.isArray(pageData) ? pageData : []);
  const totalPages = pageData?.totalPages || 1;

  const filtered = useMemo(() => {
    let list = rawList;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.title?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q) ||
          String(r.id).includes(q),
      );
    }
    return list;
  }, [rawList, search]);

  const summary = useMemo(() => {
    const all = rawList || [];
    const total = all.length;
    const drafts = all.filter((r) => r.workflowStatus === WorkflowStatus.DRAFT).length;
    const pending = all.filter((r) =>
      [
        WorkflowStatus.SUBMITTED,
        WorkflowStatus.IN_REVIEW,
        WorkflowStatus.REVISION_REQUESTED,
      ].includes(r.workflowStatus),
    ).length;
    const approved = all.filter((r) => r.workflowStatus === WorkflowStatus.APPROVED).length;
    const totalValue = all.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    return { total, drafts, pending, approved, totalValue };
  }, [rawList]);

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{r.title}</div>
          <div className="truncate text-xs text-ink-muted">#{r.id}</div>
        </div>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      render: (r) => (
        <span className="text-sm">
          {r.vendorId ? vendorMap.get(r.vendorId) || `Vendor #${r.vendorId}` : <span className="text-ink-faint">—</span>}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      render: (r) => <span className="tabular-nums">{r.quantity ?? '—'}</span>,
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      align: 'right',
      render: (r) => <CurrencyDisplay value={r.unitPrice} />,
    },
    {
      key: 'amount',
      header: 'Total Amount',
      align: 'right',
      render: (r) => (
        <span className="font-medium">
          <CurrencyDisplay value={r.amount} />
        </span>
      ),
    },
    {
      key: 'workflowStatus',
      header: 'Status',
      render: (r) => <StatusPill value={r.workflowStatus} />,
    },
    {
      key: 'requestedBy',
      header: 'Requested By',
      render: (r) =>
        r.requestedBy ? (
          <span className="text-sm">User #{r.requestedBy}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (r) => <DateDisplay value={r.createdAt} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setActiveId(r.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title="Procurement" breadcrumb="Finance / Procurement">
      <PageHeader
        title="Procurement Requests"
        description={
          isProcAdmin
            ? 'Review, approve, or create procurement requests across the company.'
            : 'Create and track your procurement requests.'
        }
        actions={
          <Button
            variant="primary"
            icon={FiPlus}
            onClick={() => setShowCreate(true)}
          >
            New request
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <SummaryStat
          label={scope === 'mine' ? 'My Requests' : 'Total Requests'}
          value={summary.total}
          icon={FiShoppingCart}
          tone="primary"
        />
        <SummaryStat
          label="Drafts"
          value={summary.drafts}
          icon={FiFilter}
          tone="warning"
        />
        <SummaryStat
          label="Pending"
          value={summary.pending}
          icon={FiUsers}
          tone="info"
        />
        <SummaryStat
          label="Approved"
          value={summary.approved}
          icon={FiUser}
          tone="success"
        />
      </div>

      {isProcAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-card">
          <button
            type="button"
            onClick={() => { setScope('all'); setPage(0); }}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === 'all'
                ? 'bg-primary text-white'
                : 'text-ink-muted hover:bg-slate-100 hover:text-ink'
              }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => { setScope('mine'); setPage(0); }}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === 'mine'
                ? 'bg-primary text-white'
                : 'text-ink-muted hover:bg-slate-100 hover:text-ink'
              }`}
          >
            Mine
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by title, description, or ID..."
          className="w-full sm:max-w-sm"
        />
        <Select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="sm:w-48"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <DataTable
          columns={columns}
          rows={filtered}
          loading={isLoading}
          error={error}
          onRowClick={(r) => setActiveId(r.id)}
          empty={
            <EmptyState
              title="No procurement requests"
              description={
                scope === 'mine'
                  ? "You haven't created any requests yet."
                  : 'No procurement requests match your filters.'
              }
              action={
                <Button
                  variant="primary"
                  icon={FiPlus}
                  onClick={() => setShowCreate(true)}
                >
                  Create your first request
                </Button>
              }
            />
          }
        />

        {(rawList.length > 0 || page > 0) && (
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <span className="text-sm text-slate-500">
                Page Size:
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">
                Page <span className="font-medium text-slate-700">{page + 1}</span> of <span className="font-medium text-slate-700">{totalPages}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                icon={FiChevronLeft}
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next <FiChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ProcurementForm
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(created) => {
          if (created?.id) setActiveId(created.id);
        }}
      />

      <ProcurementDetailDrawer
        open={!!activeId}
        onClose={() => setActiveId(null)}
        requestId={activeId}
      />
    </AppLayout>
  );
}
